import { uuidModule } from "@/modules/uuid";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IUpdateSpaceAccountSettingsOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import {
  CalendarSettings,
  SpaceAccountSettingsModelData,
  SpaceAccountSettingsPayload,
  VoiceSettings,
} from "@/store/space-account-settings/types";

export class UpdateSpaceAccountSettingsOperation extends BaseSyncOperation<IUpdateSpaceAccountSettingsOperation> {
  get operationKind(): SyncOperationKind {
    return "UPDATE_SPACE_ACCOUNT_SETTINGS";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const settingsUpdate = this.generateSpaceAccountSettingsUpdate();

    if (settingsUpdate) {
      optimisticUpdates.push(settingsUpdate);
    }

    return optimisticUpdates;
  }

  async triggerRecompute(): Promise<void> {
    await this.store.spaceAccountSettings.recompute(this.payload.space_account_id);
  }

  protected generateSpaceAccountSettingsUpdate(): OptimisticSyncUpdate<SpaceAccountSettingsModelData> | null {
    const existingVoiceSettings = this.store.spaceAccountSettings.voiceSettings;
    const existingCalendarSettings = this.store.spaceAccountSettings.calendarSettings;

    // Build the merged settings payload by applying the patch on top of existing settings
    const mergedSettings: SpaceAccountSettingsPayload = {};

    // Merge voice settings if provided in patch or existing
    if (this.payload.settings.voice || existingVoiceSettings) {
      const mergedVoice: VoiceSettings = {
        custom_vocabulary:
          this.payload.settings.voice?.custom_vocabulary ??
          existingVoiceSettings?.custom_vocabulary ??
          [],
        processing_instructions:
          this.payload.settings.voice?.processing_instructions ??
          existingVoiceSettings?.processing_instructions ??
          "",
      };
      mergedSettings.voice = mergedVoice;
    }

    // Merge calendar settings if provided in patch or existing
    if (this.payload.settings.calendar || existingCalendarSettings) {
      const mergedCalendar: CalendarSettings = {
        hide_calendar_integration_shill:
          this.payload.settings.calendar?.hide_calendar_integration_shill ??
          existingCalendarSettings?.hide_calendar_integration_shill ??
          false,
      };
      mergedSettings.calendar = mergedCalendar;
    }

    const syncUpdate: OptimisticSyncUpdate<SpaceAccountSettingsModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: {
        model_id: this.payload.space_account_id,
        model_kind: "SPACE_ACCOUNT_SETTINGS",
        model_version: 0,
        model_data: {
          owned_by_space_account_id: this.payload.space_account_id,
          settings: mergedSettings,
        },
        model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
      },
    };

    return syncUpdate;
  }
}
